import React from 'react';
import { Box, Typography } from '@mui/material';
import { BRAND_COLORS, FONTS } from '../types/index.ts';
import { fillBarColor, fillTextColor } from './fillColors.ts';

interface FillLineProps {
  utilization: number; // 0..100
  variant?: 'primary' | 'compact';
  animate?: boolean;
}

// The signature Load-Line gauge: a graduated horizontal capacity bar with a
// cyan 0–100 ruler and a marker parked at the exact fill value, the % stamped
// in Plex Mono. Vivid color lives in the bar/marker; the numeral uses an
// AA-safe darkened tint (see fillColors).
const FillLine: React.FC<FillLineProps> = ({ utilization, variant = 'primary', animate = true }) => {
  const u = Math.max(0, Math.min(100, utilization));
  const bar = fillBarColor(u);
  const text = fillTextColor(u);
  const headroom = 100 - u;
  const isPrimary = variant === 'primary';

  const track = (
    <Box>
      <Box
        sx={{
          position: 'relative',
          height: isPrimary ? 30 : 16,
          border: `1.5px solid ${BRAND_COLORS.ink}`,
          borderRadius: '3px',
          overflow: 'hidden',
          backgroundColor: BRAND_COLORS.slateSoft,
          // cyan graduation ruler (graphic only) on the empty track
          backgroundImage:
            'repeating-linear-gradient(90deg, rgba(0,166,237,0.5) 0 1px, transparent 1px 10%)',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${u}%`,
            backgroundColor: bar,
            backgroundImage:
              'repeating-linear-gradient(90deg, rgba(255,255,255,0.18) 0 1px, transparent 1px 12px)',
            transformOrigin: 'left center',
            animation: animate ? 'ld-load-x 600ms cubic-bezier(0.22,1,0.36,1) both' : 'none',
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -1,
            bottom: -1,
            left: `${u}%`,
            width: '2px',
            backgroundColor: BRAND_COLORS.ink,
            transform: 'translateX(-1px)',
          }}
        />
      </Box>
      {isPrimary && (
        <Box
          aria-hidden
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 0.5,
            fontFamily: FONTS.mono,
            fontSize: 10,
            color: BRAND_COLORS.slateText,
          }}
        >
          {[0, 20, 40, 60, 80, 100].map((n) => (
            <span key={n}>{n}</span>
          ))}
        </Box>
      )}
    </Box>
  );

  if (!isPrimary) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>{track}</Box>
        <Typography
          component="span"
          sx={{ fontFamily: FONTS.mono, fontFeatureSettings: '"tnum" 1', fontWeight: 600, fontSize: 20, color: text, lineHeight: 1 }}
        >
          {u.toFixed(1)}%
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
      <Box sx={{ flex: 1 }}>{track}</Box>
      <Box sx={{ textAlign: 'right', minWidth: 122 }}>
        <Typography
          component="div"
          sx={{ fontFamily: FONTS.display, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10, color: BRAND_COLORS.slateText }}
        >
          Space utilization
        </Typography>
        <Typography
          component="div"
          sx={{ fontFamily: FONTS.mono, fontFeatureSettings: '"tnum" 1', fontWeight: 600, fontSize: 40, lineHeight: 1.05, color: text }}
        >
          {u.toFixed(1)}%
        </Typography>
        <Typography
          component="div"
          sx={{ fontFamily: FONTS.mono, fontSize: 11, color: BRAND_COLORS.slateText, mt: 0.5 }}
        >
          headroom {headroom.toFixed(1)}%
        </Typography>
      </Box>
    </Box>
  );
};

export default FillLine;
